import { Avatar, Button, Select } from "antd";
import { Edit } from "lucide-react";
import { FC } from "react";

interface User {
  id: string | number;
  firstName?: string;
  lastName?: string;
  photoId?: string;
}

interface AssigneeSelectorProps {
  selectedTask: any;
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  editedAssigneeId: string | undefined;
  setEditedAssigneeId: (value: string | undefined) => void;
  handleSave: () => void;
  t: (key: string) => string;
  users: User[];
  /** Cloudinary base URL for avatars (`applicationStore.siteConfig.baseCloudinary`), passed down so this component has no store dependency. */
  baseCloudinary: string;
  disabled?: boolean;
}

const AssigneeSelector: FC<AssigneeSelectorProps> = ({
  selectedTask,
  isEditing,
  setIsEditing,
  editedAssigneeId,
  setEditedAssigneeId,
  handleSave,
  t,
  users,
  baseCloudinary,
  disabled = false
}) => {

  const { Option } = Select;
  // Convert the task's assigneeId to a string to ensure type consistency
  const initialAssigneeId = selectedTask?.assigneeId ? String(selectedTask.assigneeId) : undefined;

  // Start editing with the current assignee
  const handleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (disabled) return;
    setEditedAssigneeId(initialAssigneeId);
    setIsEditing(true);
  };

  // Find the current user data
  const findUserById = (userId: string | undefined): User | null => {
    if (!userId) return null;
    return users.find((user: User) => String(user.id) === String(userId)) || null;
  };

  const currentUser = findUserById(initialAssigneeId);

  // Custom filter function for improved search
  const filterOption = (input: string, option: any) => {
    const label = option?.label || '';

    // Case-insensitive search on the full name
    return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  // Render the edit mode
  if (isEditing && !disabled) {
    return (
      <div className="field-edit">
        <Select
          id = "task-assignee-select"
          className={`assignee-select ${!disabled ? "hover-effect" : ""}`}
          value={editedAssigneeId ? String(editedAssigneeId) : undefined}
          onChange={(val: string | undefined) => setEditedAssigneeId(val)}
          showSearch
          optionFilterProp="label"
          filterOption={filterOption}
          allowClear
          placeholder={t('SelectAssignee')}
          optionLabelProp="label"
          style={{ width: '100%' }}
          disabled={disabled}
        >
          {users.map((user: User) => (
            <Option
              key={user.id}
              value={String(user.id)}
              // Provide the text you want displayed in the input area:
              label={`${user.firstName || ''} ${user.lastName || ''}`.trim()}
            >
              <div className="assignee-option">
                <Avatar
                  size="small"
                  className="assignee-option-avatar"
                  src={
                    user.photoId
                      ? `${baseCloudinary}/image/upload/w_300/${user.photoId}`
                      : undefined
                  }
                >
                  {user.firstName?.[0]}
                  {user.lastName?.[0]}
                </Avatar>
                <span className="assignee-option-name">
                  {user.firstName} {user.lastName}
                </span>
              </div>
            </Option>
          ))}
        </Select>

        <div className="field-actions">
          <Button
            type="primary"
            onClick={handleSave}
            className={`field-save ${!disabled ? "hover-effect" : ""}`}
          >
            {t('Save')}
          </Button>
          <Button
            onClick={() => setIsEditing(false)}
            className={`field-cancel ${!disabled ? "hover-effect" : ""}`}
          >
            {t('Cancel')}
          </Button>
        </div>
      </div>
    );
  }

  // Render the view mode
  return (
    <div
      className={`field-view ${!disabled ? "hover-effect" : ""} group`}
      onClick={() => { if (!disabled) setIsEditing(true); }}
    >
      {currentUser ? (
        <div className="assignee-info">
          <Avatar
            size="small"
            className="assignee-avatar"
            src={
              currentUser.photoId
                ? `${baseCloudinary}/image/upload/w_300/${currentUser.photoId}`
                : undefined
            }
          >
            {currentUser.firstName?.[0]}
            {currentUser.lastName?.[0]}
          </Avatar>
          <span className="assignee-name">
            {currentUser.firstName} {currentUser.lastName}
          </span>
        </div>
      ) : (
        <span className="no-assignee">{t('NoAssignee')}</span>
      )}
      {!disabled && (
        <Button
          type="text"
          size="small"
          className={`edit-button ${!disabled ? "hover-effect" : ""}`}
          icon={<Edit size={14} />}
          onClick={handleEdit}
        />
      )}
    </div>
  );
};

export default AssigneeSelector;